import ColorHelper from '@Common/Helpers/ColorHelper';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { IControlParameters } from '@Common/Controls/BaseControl';
import { Control, IMargins } from '@Common/Controls/Controls';
import { Popup } from '@Common/Controls/Popup';
import { colorPicker } from '@Common/Controls/ColorPicker';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface IDropDownColorPickerParameters extends IControlParameters {
    isDisable?: boolean;
    isComponent?: boolean;
    titleStyle?: string;
    margins?: IMargins;
    namedColors?: boolean | IMap<string>;
    onShowContent?: () => void;
    useOpacity?: boolean;
    defaultValue?;
    resetable?;
    contentStyle?;
    lineHeight?;
    maxContentHeight?;
}

export function dropDownColorPicker(param: IDropDownColorPickerParameters): DropDownColorPicker {
    param = $.extend(
        {
            css: null,
            className: '',
            width: '100%',
            height: 30,

            useOpacity: true,

            isDisable: false,
            isComponent: false,
            titleStyle: '',
            resetable: false,
            defaultValue: null,
            margins: {},
            onShowContent: _.noop,
            namedColors: null
        },
        param
    );
    param.margins = $.extend(
        {
            left: false,
            right: false,
            top: false,
            bottom: false
        },
        param.margins
    );

    let isDisable = param.isDisable;
    let isContentVisible = false;
    let onChange = _.noop;

    if (param.namedColors === true) {
        param.namedColors = ColorHelper.getCustomColors();
    }

    let value = null;

    const titleControl = $('<div class="name" unselectable="on"/>');
    const root = $('<div class="drop-down drop-down-color-picker" unselectable="on"/>').append(titleControl);
    root.append('<span class="dropdown-arrow" unselectable="on"/>')
        .addClass(param.titleStyle)
        .css({
            height: param.height + 'px',
            lineHeight: param.height + 'px',
            width: param.width
        });

    if (param.isComponent) {
        root.addClass('component-drop-down');
    }

    root.applyMargins(param.margins as any);
    root.bind(pointerHelper.pointerDownEventName, function () {
        isContentVisible = content.is(':visible');
    }).click(function (e) {
        e.stopPropagation();

        if (!isDisable) {
            if (!isContentVisible) {
                showContent();
            } else if (content.is(':visible')) {
                removeContent();
            }
        }
    });

    const content = $('<div class="drop-down-content popup-shadow" unselectable="on"/>')
        .css({ top: param.lineHeight, position: 'absolute', maxHeight: param.maxContentHeight, minWidth: 200 })
        .addClass(param.contentStyle)
        .bind('removePopup', function () {
            removeContent();
        })
        .bind(pointerHelper.pointerDownEventName, function (e) {
            e.preventClearPopups();
        });

    const popup = new Popup({
        content: content,
        isHidden: true
    });

    popup.onClose(function () {
        root.removeClass('clicked');
        setValue(getValue());
    });

    let cp;
    let cDiv;

    const applyPopup = function () {
        if (cp != null) {
            const val = cp.getNamedColor();
            popup.hide();
            setValue(val);
        }
    };

    function toggleResetable(root, isResetable) {
        const resetBtn = $('.reset-button', root);
        if (isResetable) {
            if (resetBtn.length === 0) {
                $('<span class="reset-button" unselectable="on"/>')
                    .appendTo(root)
                    .click(function (event) {
                        event.stopPropagation();
                        setValue(param.defaultValue);
                        onChange(getValue());
                    });
            }
        } else {
            resetBtn.remove();
        }
        root.toggleClass('resetable', isResetable);
    }

    function showContent() {
        if (!cDiv) {
            cDiv = $('<div class="color-picker-popup"></div>').appendTo(content);
            cp = colorPicker({
                showButton: true,
                namedColors: param.namedColors,
                useOpacity: param.useOpacity,
                change: function () {
                    titleControl.css({ backgroundColor: ColorHelper.getStringColor(cp.getColor()) });
                }
            });

            cp.getControl().appendTo(cDiv);
            if (value) {
                cp.setNamedColor(value);
            }

            cp.onOkClick(function () {
                applyPopup();
                onChange(getValue());
            });

            cp.onCancelClick(function () {
                popup.hide();
                setValue(getValue());
            });
        }

        root.addClass('clicked');
        popup.show(root.offset().left, root.offset().top + root.outerHeight());
        param.onShowContent();
    }

    function removeContent() {
        popup.hide();
        root.removeClass('clicked');
    }

    function setValue(val) {
        value = val;
        toggleResetable(root, param.resetable && val != param.defaultValue);
        if (cp != null) {
            cp.setNamedColor(value);
            titleControl.css({ backgroundColor: ColorHelper.getStringColorHex(cp.getColor(), false) });
        } else {
            const color = ColorHelper.getRepresentationColor(val, param.namedColors as any);
            titleControl.css({ backgroundColor: color || '' });
        }
    }

    function getValue() {
        return value;
    }

    function disable() {
        root.addClass('disabled');
        isDisable = true;
    }

    function enable() {
        root.removeClass('disabled');
        isDisable = false;
    }

    return {
        getControl: function () {
            return root;
        },
        getContent: function () {
            return content;
        },

        disable: disable,
        enable: enable,

        hideContent: removeContent,
        getColor: function () {
            return ColorHelper.getRepresentationColor(value, param.namedColors as any);
        },
        getValue: getValue,
        setValue: setValue,
        onChange: function (fn) {
            if ($.isFunction(fn)) {
                onChange = fn;
            }
        },
        setState: function (enabled) {
            if (enabled) {
                enable();
            } else {
                disable();
            }
        },
        destroy: function () {
            safeDestroys(root, popup, cp);
        }
    };
}

export interface DropDownColorPicker extends Control {
    getContent(): JQuery;

    disable(): void;

    enable(): void;

    hideContent(): void;

    getColor(): string;

    getValue(): any;

    setValue(value: any);

    onChange(fn: (value: any) => void);

    destroy(): void;
}
